import { Fragment } from "react";
import { MaterialPicker } from "@/components/modelsheets/MaterialPicker";
import { num } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { fabricGroups, fabricLabel, markerPieces, requiredBySize, linesToRows } from "@/lib/cutTickets";

const PHASES = { programmato: "Programmato", taglio: "Taglio", confezione: "Confezione", controllo: "Controllo", completato: "Completato" };

const ColorCell = ({ value, colors, used, onChange, i }) => {
  const items = colors.filter((c) => c.toLowerCase() === (value || "").toLowerCase() || !used.includes(c.toLowerCase())).map((c) => ({ name: c }));
  return (
    <div className="min-w-[160px]">
      <MaterialPicker items={items} kind="tipo" value={value} onChange={onChange} onPick={(m) => onChange(m.name)} placeholder="Scrivi o scegli il colore" testid={`ticket-line-${i}-color`} />
    </div>
  );
};

export const QuantitiesTable = ({ lines, sizes, markers, colors = [], phases = {}, onChange }) => {
  const req = requiredBySize(linesToRows(lines, sizes), sizes);
  // Ogni tessuto deve arrivare da solo ai capi da tagliare: conteggio separato per tessuto
  const groups = fabricGroups(markers).map((g) => {
    const ms = markers.filter((m) => fabricLabel(m) === g.label);
    const sized = Object.fromEntries(sizes.map((z) => [z, ms.reduce((t, m) => t + markerPieces(m, z), 0)]));
    const free = ms.some((m) => m.free_sizes && Number(m.layers) > 0);
    return { label: g.label, cut: Object.fromEntries(sizes.map((z) => [z, sized[z] + (free ? Math.max(0, req[z] - sized[z]) : 0)])) };
  });
  const total = Object.values(req).reduce((a, b) => a + b, 0);
  const anyMarker = markers.some((m) => Number(m.layers) > 0);
  const set = (i, z, v) => onChange(lines.map((l, j) => (j === i ? { ...l, sizes: { ...l.sizes, [z]: v } } : l)));
  const setColor = (i, color) => onChange(lines.map((l, j) => (j === i ? { ...l, color, fabrics: {} } : l)));
  const used = lines.map((l) => l.color.trim().toLowerCase()).filter(Boolean);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">Scrivi il colore: si apre l'elenco dei colori dei tessuti in magazzino. Con il + aggiungi un altro colore.</p>
        {lines.length === 0 && <Button size="sm" onClick={() => onChange([{ color: "", sizes: {} }])} data-testid="ticket-add-color"><Plus className="mr-1 h-3.5 w-3.5" />Aggiungi colore</Button>}
      </div>
      <div className="overflow-x-auto rounded-md border border-border md:overflow-visible">
        <table className="w-full text-sm" data-testid="ticket-quantities">
          <thead><tr className="border-b border-border text-xs text-muted-foreground">
            <th className="p-2 text-left font-medium">Colore</th>{sizes.map((z) => <th key={z} className="w-16 p-2 font-medium">{z}</th>)}<th className="w-16 p-2 font-medium">Totale</th><th className="w-16" />
          </tr></thead>
          <tbody>
            {lines.map((l, i) => {
              const ph = l.color && phases[l.color.toLowerCase()];
              const locked = ph && ph !== "programmato";
              const tot = sizes.reduce((a, z) => a + (Number(l.sizes[z]) || 0), 0);
              return (
                <Fragment key={i}>
                <tr className={`border-b border-border/50 ${i % 2 ? "" : "bg-secondary/20"}`} data-testid={`ticket-line-${i}`}>
                  <td className="p-1">{locked || (ph && l.color) ? <span className="px-1 font-medium">{l.color}</span> : <ColorCell value={l.color} colors={colors} used={used} i={i} onChange={(c) => setColor(i, c)} />}{ph && <Badge variant="outline" className="ml-2 text-[10px]">{PHASES[ph] || ph}</Badge>}</td>
                  {sizes.map((z) => (
                    <td key={z} className="p-1"><Input type="number" min="0" disabled={locked} value={l.sizes[z] ?? ""} onChange={(e) => set(i, z, e.target.value)} className="no-spin h-8 min-w-[3.25rem] px-1 text-center" data-testid={`ticket-qty-${i}-${z}`} /></td>
                  ))}
                  <td className="p-2 text-center font-semibold">{num(tot)}</td>
                  <td className="whitespace-nowrap p-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" title="Aggiungi un colore" onClick={() => onChange([...lines.slice(0, i + 1), { color: "", sizes: {} }, ...lines.slice(i + 1)])} data-testid={`ticket-line-add-${i}`}><Plus className="h-3.5 w-3.5" /></Button>
                    {!locked && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" title="Togli questo colore" onClick={() => onChange(lines.filter((_, j) => j !== i))} data-testid={`ticket-line-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </td>
                </tr>
                </Fragment>
              );
            })}
            <tr className="border-b border-border/50 bg-secondary/40 font-semibold">
              <td className="p-2">Totale da tagliare</td>{sizes.map((z) => <td key={z} className="p-2 text-center">{req[z] || ""}</td>)}<td className="p-2 text-center" data-testid="ticket-total-pieces">{num(total)}</td><td />
            </tr>
            {anyMarker && groups.map((g, gi) => (
              <tr key={g.label} className="text-xs" data-testid={`ticket-cut-row-${gi}`}>
                <td className="p-2 text-muted-foreground">Dai grafici · <span className="font-medium text-foreground">{g.label}</span></td>
                {sizes.map((z) => (
                  <td key={z} className={`p-2 text-center font-semibold ${g.cut[z] === req[z] ? "text-emerald-400" : g.cut[z] < req[z] ? "text-rose-400" : "text-amber-400"}`} data-testid={gi ? `ticket-cut-${gi}-${z}` : `ticket-cut-${z}`}>{g.cut[z] || (req[z] ? 0 : "")}</td>
                ))}
                <td /><td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {Object.values(phases).some((p) => p && p !== "programmato") && <p className="text-[11px] text-muted-foreground">I colori con il lotto già in taglio o oltre non si modificano più da qui: cambiali dal pannello Produzione.</p>}
    </div>
  );
};
